import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { TabView, TabPanel } from 'primereact/tabview';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputSwitch } from 'primereact/inputswitch';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { Tag } from 'primereact/tag';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import {
  getWebsitePages, saveWebsitePage, deleteWebsitePage,
  getWebsiteSettings, saveWebsiteSettings, uploadWebsiteImages,
} from '../../api/websiteApi';
import MediaPickerDialog from '../Media/MediaPickerDialog';
import './WebsiteManager.scss';

const BLOCK_TYPES = [
  { label: 'Hero banner', value: 'hero' },
  { label: 'Text section', value: 'text' },
  { label: 'Image gallery', value: 'images' },
  { label: 'Contact form + details', value: 'contact' },
];

const emptyBlock = (type) => {
  switch (type) {
    case 'hero': return { type, heading: '', subheading: '', ctaText: 'Get in touch', ctaLink: '/contact', image: '' };
    case 'text': return { type, heading: '', body: '' };
    case 'images': return { type, heading: '', items: [] };
    default: return { type, heading: 'Contact Us' };
  }
};

const WebsiteManager = () => {
  const [pages, setPages] = useState([]);
  const [settings, setSettings] = useState({ websiteEnabled: true, customDomain: null, website: {} });
  const [editing, setEditing] = useState(null); // working copy of the page being edited
  const [saving, setSaving] = useState(false);
  const [uploadingIdx, setUploadingIdx] = useState(null);
  const [newPageOpen, setNewPageOpen] = useState(false);
  const [newPage, setNewPage] = useState({ slug: '', title: '' });
  const fileRef = useRef(null);
  const pendingUpload = useRef(null); // { blockIdx, mode: 'gallery' | 'single' | 'logo' }
  // Same targets as pendingUpload, but filled from the shared media library
  // instead of a fresh upload — that's the point of the library.
  const [pickerTarget, setPickerTarget] = useState(null);

  const load = async () => {
    try {
      const [p, s] = await Promise.all([getWebsitePages(), getWebsiteSettings()]);
      setPages(p.pages || []);
      setSettings(s);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not load website content');
    }
  };
  useEffect(() => { load(); }, []);

  // ---- settings -------------------------------------------------------------
  const setSite = (key, value) =>
    setSettings((s) => ({ ...s, website: { ...s.website, [key]: value } }));

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await saveWebsiteSettings({ websiteEnabled: settings.websiteEnabled, ...settings.website });
      toast.success('Website settings saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ---- page editing ---------------------------------------------------------
  const openEditor = (page) => {
    setEditing({
      slug: page.slug,
      title: page.title,
      meta_description: page.meta_description || '',
      is_published: page.is_published !== false,
      show_in_nav: page.show_in_nav !== false,
      sort_order: page.sort_order ?? 0,
      blocks: JSON.parse(JSON.stringify(page.blocks || [])),
    });
  };

  const handleSavePage = async () => {
    setSaving(true);
    try {
      await saveWebsitePage(editing.slug, editing);
      toast.success(`"${editing.title}" saved`);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePage = (page) => {
    const isCore = page.slug === 'home' || page.slug === 'contact';
    confirmDialog({
      message: isCore
        ? `Reset "${page.title}" to the default content?`
        : `Delete the "${page.title}" page from your website?`,
      header: isCore ? 'Reset page' : 'Delete page',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          await deleteWebsitePage(page.slug);
          toast.success(isCore ? 'Page reset to default' : 'Page deleted');
          load();
        } catch (err) {
          toast.error(err.response?.data?.message || 'Delete failed');
        }
      },
    });
  };

  const handleCreatePage = async () => {
    const slug = newPage.slug.trim().toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-+|-+$/g, '');
    if (!slug || !newPage.title.trim()) {
      toast.warn('Both a title and a URL name are required');
      return;
    }
    try {
      await saveWebsitePage(slug, {
        title: newPage.title.trim(),
        blocks: [emptyBlock('text')],
        sort_order: pages.length,
      });
      setNewPageOpen(false);
      setNewPage({ slug: '', title: '' });
      toast.success('Page created');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Create failed');
    }
  };

  // ---- blocks ---------------------------------------------------------------
  const setBlock = (idx, patch) =>
    setEditing((e) => ({
      ...e,
      blocks: e.blocks.map((b, i) => (i === idx ? { ...b, ...patch } : b)),
    }));

  const moveBlock = (idx, dir) =>
    setEditing((e) => {
      const blocks = [...e.blocks];
      const j = idx + dir;
      if (j < 0 || j >= blocks.length) return e;
      [blocks[idx], blocks[j]] = [blocks[j], blocks[idx]];
      return { ...e, blocks };
    });

  const removeBlock = (idx) =>
    setEditing((e) => ({ ...e, blocks: e.blocks.filter((_, i) => i !== idx) }));

  const startUpload = (blockIdx, mode) => {
    pendingUpload.current = { blockIdx, mode };
    fileRef.current?.click();
  };

  /** Route one or more image URLs to whatever asked for them. */
  const applySrcs = (target, srcs) => {
    if (!srcs.length) return;
    if (target.mode === 'single') {
      setBlock(target.blockIdx, { image: srcs[0] });
    } else if (target.mode === 'logo') {
      setSite('logo', srcs[0]);
    } else {
      setEditing((ed) => ({
        ...ed,
        blocks: ed.blocks.map((b, i) =>
          i === target.blockIdx
            ? { ...b, items: [...(b.items || []), ...srcs.map((src) => ({ src, caption: '' }))] }
            : b
        ),
      }));
    }
  };

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    const target = pendingUpload.current;
    if (!files.length || !target) return;
    setUploadingIdx(target.blockIdx);
    try {
      const res = await uploadWebsiteImages(files);
      const srcs = res.files.map((f) => f.src);
      applySrcs(target, srcs);
      toast.success(`${srcs.length} image(s) uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploadingIdx(null);
      pendingUpload.current = null;
    }
  };

  // ---- render ---------------------------------------------------------------
  const publicUrl = settings.customDomain ? `https://${settings.customDomain}` : null;

  const renderBlockEditor = (block, idx) => (
    <div key={idx} className="website-block">
      <div className="flex align-items-center gap-2 mb-2">
        <Tag value={BLOCK_TYPES.find((t) => t.value === block.type)?.label || block.type} />
        <div className="ml-auto flex gap-1">
          <Button icon="pi pi-arrow-up" text size="small" disabled={idx === 0} onClick={() => moveBlock(idx, -1)} />
          <Button icon="pi pi-arrow-down" text size="small" disabled={idx === editing.blocks.length - 1} onClick={() => moveBlock(idx, 1)} />
          <Button icon="pi pi-trash" text size="small" severity="danger" onClick={() => removeBlock(idx)} />
        </div>
      </div>

      {(block.type === 'hero' || block.type === 'text' || block.type === 'images' || block.type === 'contact') && (
        <InputText className="w-full mb-2" placeholder="Heading" value={block.heading || ''}
          onChange={(e) => setBlock(idx, { heading: e.target.value })} />
      )}

      {block.type === 'hero' && (
        <>
          <InputTextarea className="w-full mb-2" rows={2} placeholder="Subheading" value={block.subheading || ''}
            onChange={(e) => setBlock(idx, { subheading: e.target.value })} />
          <div className="flex gap-2 mb-2">
            <InputText className="flex-1" placeholder="Button text" value={block.ctaText || ''}
              onChange={(e) => setBlock(idx, { ctaText: e.target.value })} />
            <Dropdown value={block.ctaLink || '/contact'} onChange={(e) => setBlock(idx, { ctaLink: e.value })}
              options={[{ label: 'Contact page', value: '/contact' }, { label: 'Portal login', value: '/login' }]} />
          </div>
          <div className="flex align-items-center gap-2">
            <Button label={block.image ? 'Change banner image' : 'Add banner image'} icon="pi pi-image"
              size="small" outlined loading={uploadingIdx === idx} onClick={() => startUpload(idx, 'single')} />
            <Button label="From library" icon="pi pi-images" size="small" text
              onClick={() => setPickerTarget({ blockIdx: idx, mode: 'single' })} />
            {block.image && <img src={block.image} alt="" className="website-thumb" />}
          </div>
        </>
      )}

      {block.type === 'text' && (
        <InputTextarea className="w-full" rows={6}
          placeholder="Body text. Leave a blank line between paragraphs."
          value={block.body || ''} onChange={(e) => setBlock(idx, { body: e.target.value })} />
      )}

      {block.type === 'images' && (
        <>
          <div className="flex align-items-center gap-2 mb-2">
            <Button label="Add images" icon="pi pi-upload" size="small" outlined
              loading={uploadingIdx === idx} onClick={() => startUpload(idx, 'gallery')} />
            <Button label="From library" icon="pi pi-images" size="small" text
              onClick={() => setPickerTarget({ blockIdx: idx, mode: 'gallery' })} />
          </div>
          <div className="website-gallery">
            {(block.items || []).map((item, ii) => (
              <div key={ii} className="website-gallery-item">
                <img src={item.src} alt="" />
                <InputText placeholder="Caption" value={item.caption || ''}
                  onChange={(e) => {
                    const items = [...block.items];
                    items[ii] = { ...items[ii], caption: e.target.value };
                    setBlock(idx, { items });
                  }} />
                <Button icon="pi pi-times" text size="small" severity="danger"
                  onClick={() => setBlock(idx, { items: block.items.filter((_, j) => j !== ii) })} />
              </div>
            ))}
          </div>
        </>
      )}

      {block.type === 'contact' && (
        <small className="text-color-secondary">
          Renders the enquiry form plus the contact details from Site Settings.
        </small>
      )}
    </div>
  );

  return (
    <div className="p-3">
      <ConfirmDialog />
      <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={handleFiles} />

      <Card>
        <div className="flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <div>
            <h1 className="text-2xl m-0">Website</h1>
            <p className="text-color-secondary m-0 mt-1 text-sm">
              Manage the public pages visitors see on your school's domain.
            </p>
          </div>
          {publicUrl && (
            <a href={publicUrl} target="_blank" rel="noreferrer">
              <Button label="View site" icon="pi pi-external-link" outlined size="small" />
            </a>
          )}
        </div>

        <TabView>
          <TabPanel header="Pages">
            <div className="flex justify-content-end mb-2">
              <Button label="New Page" icon="pi pi-plus" size="small" onClick={() => setNewPageOpen(true)} />
            </div>
            <div className="grid">
              {pages.map((p) => (
                <div key={p.slug} className="col-12 md:col-6 lg:col-4">
                  <div className="website-page-card">
                    <div className="flex align-items-center gap-2">
                      <span className="font-semibold">{p.title}</span>
                      {p.isDefault && <Tag severity="info" value="default content" />}
                      {!p.is_published && !p.isDefault && <Tag severity="warning" value="draft" />}
                    </div>
                    <div className="text-sm text-color-secondary mb-3">/{p.slug === 'home' ? '' : p.slug}</div>
                    <div className="flex gap-2">
                      <Button label="Edit" size="small" onClick={() => openEditor(p)} />
                      {!p.isDefault && (
                        <Button label={p.slug === 'home' || p.slug === 'contact' ? 'Reset' : 'Delete'}
                          size="small" text severity="danger" onClick={() => handleDeletePage(p)} />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabPanel>

          <TabPanel header="Site Settings">
            <div className="grid" style={{ maxWidth: 720 }}>
              <div className="col-12 flex align-items-center gap-3 mb-2">
                <InputSwitch checked={settings.websiteEnabled}
                  onChange={(e) => setSettings((s) => ({ ...s, websiteEnabled: e.value }))} />
                <div>
                  <div className="font-semibold">Public website enabled</div>
                  <small className="text-color-secondary">
                    When off, your domain shows the portal login instead.
                  </small>
                </div>
              </div>

              <div className="col-12">
                <label className="block text-sm font-semibold mb-1">Domain</label>
                <InputText className="w-full" value={settings.customDomain || ''} disabled
                  placeholder="Not configured — contact platform support to connect your domain" />
              </div>

              {[
                ['tagline', 'Tagline (shown on the home banner)'],
                ['address', 'Address'],
                ['phone', 'Phone'],
                ['email', 'Public contact email (also receives enquiries)'],
                ['hours', 'Opening hours'],
                ['footerText', 'Footer text'],
              ].map(([key, label]) => (
                <div key={key} className="col-12 md:col-6">
                  <label className="block text-sm font-semibold mb-1">{label}</label>
                  <InputText className="w-full" value={settings.website?.[key] || ''}
                    onChange={(e) => setSite(key, e.target.value)} />
                </div>
              ))}

              <div className="col-12 md:col-6">
                <label className="block text-sm font-semibold mb-1">Accent colour</label>
                <div className="flex gap-2 align-items-center">
                  <input type="color" value={settings.website?.accentColor || '#4f46e5'}
                    onChange={(e) => setSite('accentColor', e.target.value)}
                    style={{ width: 40, height: 34, border: 'none', background: 'none', cursor: 'pointer' }} />
                  <InputText value={settings.website?.accentColor || ''} placeholder="#4f46e5"
                    onChange={(e) => setSite('accentColor', e.target.value)} />
                </div>
              </div>

              <div className="col-12 md:col-6">
                <label className="block text-sm font-semibold mb-1">Logo</label>
                <div className="flex align-items-center gap-2">
                  <Button label="Upload logo" icon="pi pi-image" size="small" outlined
                    onClick={() => { pendingUpload.current = { blockIdx: -1, mode: 'logo' }; fileRef.current?.click(); }} />
                  <Button label="From library" icon="pi pi-images" size="small" text
                    onClick={() => setPickerTarget({ blockIdx: -1, mode: 'logo' })} />
                  {settings.website?.logo && <img src={settings.website.logo} alt="" className="website-thumb" />}
                </div>
              </div>

              <div className="col-12">
                <Button label="Save Settings" icon="pi pi-check" loading={saving} onClick={handleSaveSettings} />
              </div>
            </div>
          </TabPanel>
        </TabView>
      </Card>

      {/* Page editor */}
      <Dialog header={editing ? `Edit: ${editing.title}` : ''} visible={!!editing} style={{ width: 'min(860px, 95vw)' }}
        onHide={() => setEditing(null)} modal>
        {editing && (
          <>
            <div className="grid mb-2">
              <div className="col-12 md:col-6">
                <label className="block text-sm font-semibold mb-1">Page title</label>
                <InputText className="w-full" value={editing.title}
                  onChange={(e) => setEditing((ed) => ({ ...ed, title: e.target.value }))} />
              </div>
              <div className="col-12 md:col-6">
                <label className="block text-sm font-semibold mb-1">Search description</label>
                <InputText className="w-full" value={editing.meta_description}
                  onChange={(e) => setEditing((ed) => ({ ...ed, meta_description: e.target.value }))} />
              </div>
              <div className="col-6 flex align-items-center gap-2">
                <InputSwitch checked={editing.is_published}
                  onChange={(e) => setEditing((ed) => ({ ...ed, is_published: e.value }))} />
                <span className="text-sm">Published</span>
              </div>
              <div className="col-6 flex align-items-center gap-2">
                <InputSwitch checked={editing.show_in_nav}
                  onChange={(e) => setEditing((ed) => ({ ...ed, show_in_nav: e.value }))} />
                <span className="text-sm">Show in menu</span>
              </div>
            </div>

            {editing.blocks.map(renderBlockEditor)}

            <div className="flex gap-2 mt-3 flex-wrap">
              {BLOCK_TYPES.map((t) => (
                <Button key={t.value} label={`+ ${t.label}`} size="small" text
                  onClick={() => setEditing((ed) => ({ ...ed, blocks: [...ed.blocks, emptyBlock(t.value)] }))} />
              ))}
            </div>

            <div className="flex justify-content-end gap-2 mt-4">
              <Button label="Cancel" text onClick={() => setEditing(null)} />
              <Button label="Save Page" icon="pi pi-check" loading={saving} onClick={handleSavePage} />
            </div>
          </>
        )}
      </Dialog>

      {/* New page dialog */}
      <Dialog header="New Page" visible={newPageOpen} style={{ width: 'min(420px, 95vw)' }}
        onHide={() => setNewPageOpen(false)} modal>
        <label className="block text-sm font-semibold mb-1">Page title</label>
        <InputText className="w-full mb-3" value={newPage.title}
          onChange={(e) => setNewPage((p) => ({ ...p, title: e.target.value }))} placeholder="e.g. Admissions" />
        <label className="block text-sm font-semibold mb-1">URL name</label>
        <InputText className="w-full mb-1" value={newPage.slug}
          onChange={(e) => setNewPage((p) => ({ ...p, slug: e.target.value }))} placeholder="e.g. admissions" />
        <small className="text-color-secondary">Lowercase letters, numbers and dashes only.</small>
        <div className="flex justify-content-end gap-2 mt-3">
          <Button label="Cancel" text onClick={() => setNewPageOpen(false)} />
          <Button label="Create" onClick={handleCreatePage} />
        </div>
      </Dialog>

      {/* Reuse an image already in the school's media library */}
      <MediaPickerDialog
        visible={!!pickerTarget}
        onHide={() => setPickerTarget(null)}
        typeLock="image"
        publicUse
        onSelect={(item) => applySrcs(pickerTarget, [item.file_path])}
      />
    </div>
  );
};

export default WebsiteManager;
